import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { painterStatusLabel } from '../../../../shared/contracts/painter-status.js';

/**
 * M08-008 §16 — Execution tracking for a job.
 *
 * Routed by the vendor assignment id; resolves jobRequestId from the assignment
 * detail. Shows the execution timeline (GET /v1/vendor/execution/:jobRequestId/timeline)
 * and posts a status update against a painter assignment
 * (POST /v1/vendor/execution).
 */
interface TimelineEntry {
  readonly trackingId: string;
  readonly painterAssignmentId: string | null;
  readonly executionStatus: string;
  readonly remarks: string | null;
  readonly reportedAt: string;
}

interface PainterAssignment {
  readonly painterAssignmentId: string;
  readonly painterUserId: string;
  readonly painterName: string | null;
  readonly painterCode: string | null;
  readonly status: string;
}

const STATUSES = ['NOT_STARTED', 'ON_TRACK', 'IN_PROGRESS', 'DELAYED', 'COMPLETED_PENDING_SUBMISSION'] as const;

export function VendorExecutionPage(): ReactNode {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();

  const [jobRequestId, setJobRequestId] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<readonly TimelineEntry[]>([]);
  const [painters, setPainters] = useState<readonly PainterAssignment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const [painterAssignmentId, setPainterAssignmentId] = useState('');
  const [executionStatus, setExecutionStatus] = useState<(typeof STATUSES)[number]>('ON_TRACK');
  const [remarks, setRemarks] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadTimeline = useCallback(async (job: string) => {
    const res = await apiRequest<{ items: TimelineEntry[] }>(`/v1/vendor/execution/${encodeURIComponent(job)}/timeline`, { token });
    setTimeline(res.items);
  }, [token]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const asg = await apiRequest<{ jobRequestId: string }>(`/v1/vendor-assignments/${assignmentId}`, { token });
        if (!alive) return;
        setJobRequestId(asg.jobRequestId);
        const [, list] = await Promise.all([
          loadTimeline(asg.jobRequestId),
          apiRequest<{ items: PainterAssignment[] }>(`/v1/painter-assignments?jobRequestId=${encodeURIComponent(asg.jobRequestId)}`, { token }),
        ]);
        if (alive) { setPainters(list.items); setLoaded(true); }
      } catch (e) {
        if (alive) { setError((e as Error).message); setLoaded(true); }
      }
    })();
    return () => { alive = false; };
  }, [assignmentId, token, loadTimeline]);

  async function submit() {
    setFormError(null);
    if (!painterAssignmentId) { setFormError(t('vops.execution.painterRequired')); return; }
    setBusy(true);
    try {
      await apiRequest('/v1/vendor/execution', {
        method: 'POST',
        token,
        body: { painterAssignmentId, executionStatus, remarks: remarks.trim() || null },
      });
      setOk(true);
      setRemarks('');
      if (jobRequestId) await loadTimeline(jobRequestId);
    } catch (e) {
      setFormError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!loaded) return <main aria-label="vendor-execution"><p>{t('common.loading')}</p></main>;

  return (
    <main aria-label="vendor-execution">
      <h1>{t('vops.execution.title')}</h1>
      <p><Link to={`/vendor-ops/jobs/${assignmentId}`} aria-label="back-to-job">{t('vops.common.backToJob')}</Link></p>

      {error ? <p role="alert">{error}</p> : null}

      <section aria-label="execution-timeline">
        <h2>{t('vops.execution.timeline')}</h2>
        {timeline.length === 0 ? (
          <p aria-label="timeline-empty">{t('vops.execution.empty')}</p>
        ) : (
          <ul aria-label="timeline-list">
            {timeline.map((entry) => (
              <li key={entry.trackingId} aria-label={`timeline-${entry.trackingId}`}>
                <span aria-label={`timeline-status-${entry.trackingId}`}>{entry.executionStatus}</span>{' · '}
                {at(entry.reportedAt)}
                {entry.remarks ? <> · {entry.remarks}</> : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label="execution-update">
        <h2>{t('vops.execution.post')}</h2>
        {ok ? <p role="status" aria-label="execution-result">{t('vops.execution.recorded')}</p> : null}
        {formError ? <p role="alert">{formError}</p> : null}

        <label htmlFor="exec-painter">{t('vops.execution.painterAssignment')}</label>
        <select id="exec-painter" aria-label="exec-painter" value={painterAssignmentId}
                onChange={(e) => setPainterAssignmentId(e.target.value)} disabled={busy}>
          <option value="">{t('vops.execution.selectPainter')}</option>
          {painters.map((p) => (
            <option key={p.painterAssignmentId} value={p.painterAssignmentId}>{p.painterName ?? p.painterUserId}{p.painterCode ? ` (${p.painterCode})` : ''} — {painterStatusLabel(t, p.status)}</option>
          ))}
        </select>

        <label htmlFor="exec-status">{t('vops.common.status')}</label>
        <select id="exec-status" aria-label="exec-status" value={executionStatus}
                onChange={(e) => setExecutionStatus(e.target.value as (typeof STATUSES)[number])} disabled={busy}>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>

        <label htmlFor="exec-remarks">{t('vops.common.remarksOptional')}</label>
        <textarea id="exec-remarks" aria-label="exec-remarks" value={remarks}
                  onChange={(e) => setRemarks(e.target.value)} disabled={busy} />

        <Button type="button" aria-label="exec-btn" onClick={submit} disabled={busy} variant="primary">{t('vops.execution.recordBtn')}</Button>
      </section>
    </main>
  );
}
